import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ExternalLink, ArrowLeft } from "lucide-react";
import PageLayout from "@/components/PageLayout";
import { releases } from "@/data/mockData";

const MusicDetail = () => {
  const { id } = useParams();
  const release = releases.find((r) => r.id === id);

  if (!release) {
    return (
      <PageLayout>
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-3xl font-bold text-foreground">Release not found</h1>
          <Link to="/music" className="text-muted-foreground hover:text-foreground mt-4 inline-block">
            ← Back to Music
          </Link>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="max-w-7xl mx-auto">
        <Link to="/music" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" /> Back to Music
        </Link>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Sticky info */}
          <div className="lg:sticky lg:top-24 lg:self-start space-y-8 order-2 lg:order-1">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-2">{release.title}</h1>
              <p className="text-muted-foreground text-lg">
                {release.type} · {release.date}
              </p>
              <p className="text-sm text-muted-foreground mt-1">{release.status}</p>
            </div>

            <p className="text-muted-foreground leading-relaxed">{release.description}</p>

            {/* Streaming */}
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-widest mb-4">
                Listen
              </h3>
              <div className="space-y-3">
                {release.platforms.map((p) => (
                  <a
                    key={p.name}
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between glass-card-hover p-4 group"
                  >
                    <span className="text-foreground font-medium">{p.name}</span>
                    <ExternalLink className="w-5 h-5 text-muted-foreground group-hover:text-foreground transition-colors" />
                  </a>
                ))}
              </div>
            </div>

            {/* Merch */}
            {release.merchOptions.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-widest mb-4">
                  Buy Physical
                </h3>
                <div className="space-y-3">
                  {release.merchOptions.map((m) => (
                    <div
                      key={m.name}
                      className="flex items-center justify-between glass-card p-4"
                    >
                      <span className="text-foreground font-medium text-sm">{m.name}</span>
                      <span className="text-muted-foreground font-semibold text-sm">{m.price}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Image gallery */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-4 order-1 lg:order-2"
          >
            <div className="aspect-square rounded-lg overflow-hidden">
              <img
                src={release.image}
                alt={release.title}
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </PageLayout>
  );
};

export default MusicDetail;
